import { CLEAR_LENS, defaultAppearance, lensOnly, type AppearanceChoice } from '../config/appearance';
import type { CatalogueLine } from '../config/collections';
import { Quaternion, Vector3 } from 'three';
import { enableAnalyticsDebug, track } from '../analytics/events';
import { CameraController, CameraError } from '../camera/CameraController';
import { FrameGrabber } from '../camera/FrameGrabber';
import type { FrameFit, FrameSpec } from '../config/frames';
import { PerfMonitor } from '../perf/PerfMonitor';
import { TraceRecorder } from '../perf/TrackingTrace';
import { QualityGovernor, QUALITY_LEVELS, type QualityLevel } from '../perf/QualityGovernor';
import { StartupPhase } from '../perf/StartupPhase';
import { EngineHealth, LevelHistory, type HealthProblem, type ReloadProblem } from '../perf/EngineHealth';
import { project } from '../pose/CameraIntrinsics';
import { createSolvedPose, FramePoseSolver, type SolvedFramePose } from '../pose/FramePoseSolver';
import { measureIris } from '../pose/IrisScale';
import { sizeHandmade } from '../pose/Sizing';
import { AppearanceControls, acetateName } from '../ui/AppearanceControls';
import { LensView } from '../lensview/LensView';
import { isSunsetLens, lensViewDensity } from '../lensview/lensFilter';
import { ControlShell, type FramesViewHandle } from '../ui/ControlShell';
import { FrameSwap } from '../render/FrameSwap';
import { TempleEngraving } from '../render/TempleEngraving';
import { FrameAppearance } from '../render/FrameAppearance';
import { SizingControls, type SizingSelection } from '../ui/SizingControls';
import { loadFrame, type LoadReport } from '../render/FrameModel';
import type { FrameObject } from '../render/FrameObject';
import { DEFAULT_OCCLUDER_PARAMS } from '../render/HeadOccluder';
import { Renderer } from '../render/Renderer';
import { compilePrograms, prepareFrame, uploadTextures, warmDraw } from '../render/ShaderWarmup';
import { PoseSmoother, type SmoothedPose } from '../smoothing/PoseSmoother';
import { FaceTracker, type TrackerStatus } from '../tracking/FaceTracker';
import { LM, lmX, lmY } from '../tracking/landmarks';
import type { TrackerResult } from '../tracking/protocol';
import { TrackingStateMachine, type TrackingState } from '../tracking/TrackingStateMachine';
import { DebugPanel, type DebugStats } from '../ui/DebugPanel';
import { detectShop, OrderPanel } from '../order/OrderPanel';
import { signatureProductHref } from '../order/signatureProduct';
import { buildOrderSnapshot } from '../order/snapshot';
import { EngraveSession } from '../engrave/EngraveSession';
import { EngravePanel } from '../engrave/EngravePanel';
import { EngraveBand } from '../engrave/EngraveBand';
import { EngraveScreen } from '../engrave/EngraveScreen';
import { engravingSheet, workshopPage } from '../engrave/orderSheet';
import { acetateSpec } from '../config/acetates';
import { detectLang, setLang, type StringKey } from '../ui/i18n';
import { buildOverlay, showError, showHint, type OverlayElements } from '../ui/Overlay';
import { words } from '../ui/words';
import { HeadSetup } from '../ui/HeadSetup';
import { modalFocus } from '../ui/modalFocus';

/** Frame opacity the warm-up compiles and draws at: the glasses' first real draws are a fade-in (three bakes `transparent` into programs). */
const WARM_FADE_OPACITY = 0.5;

/** A110X wake-up after a sleep (see wakeUp / EngineHealth). */
/** A visible page that did not draw for this long has slept (app switcher, frozen page). */
const STALL_MS = 1500;
/** Hidden at least this long, or stalled at least `FULL_WAKE_STALL_MS`: fresh tracking before the glasses come back. */
const FULL_WAKE_HIDDEN_MS = 1000;
const FULL_WAKE_STALL_MS = 5000;
/** Asleep at least this long: the tracker is restarted outright (what a page reload cured). */
const TRACKER_RESTART_SLEEP_MS = 30000;
/** The quality given back is the one in effect this long before the sleep (before the stall that may precede it). */
const LEVEL_LOOKBACK_MS = 6000;
/** A110Y: automatic restarts (camera or tracker) allowed per window; past it, « actualiser la page ». */
const MAX_RESTARTS_PER_WINDOW = 3;
const RESTART_WINDOW_MS = 5 * 60_000;

/** A200: models with a Colours panel — opted-in Handmade (acetates and lenses), Signature (lenses). */
function hasAppearance(spec: FrameSpec): boolean {
  return (spec.line === 'Handmade' && Boolean(spec.customizable)) || lensOnly(spec);
}

export interface TryOnConfig {
  frame: FrameSpec;
  /** A200: the collection of the model list (Handmade or Signature = data line `Classic`); never mixed. */
  catalogueLine?: CatalogueLine;
  /**
   * A200: opened from an optical (eyeglasses) product page — the Signature frame starts with
   * « Lunettes de vue » (clear lenses), and « Original » brings that setting back.
   */
  opticalLenses?: boolean;
  /** Host opt-in (A42): show the model rail inside an embedded try-on, restricted to `catalogueLine`. */
  framePicker?: boolean;
  /** Resolve a catalogue entry before building any geometry. */
  loadFrameSpec?: () => Promise<FrameSpec>;
  /** 'en' | 'fr' | 'id' — auto-detected when omitted. */
  lang?: string;
  /** Base URL of the static assets (wasm/, models/, draco/, basis/). Defaults to the app's own base. */
  assetBase?: string;
  /** Private order upload endpoint, configured by the host after service installation. */
  orderDossierUrl?: string;
  dossierRetentionDays?: number;
  /** Actual storefront product; rendering may use an optical product's solar twin. */
  orderProductHandle?: string;
  /**
   * Explicit asset URLs for flat CDNs (Shopify Files): when set, they take
   * precedence over `assetBase`. All absolute.
   */
  assets?: {
    workerUrl?: string;
    wasmLoaderPath?: string;
    wasmBinaryPath?: string;
    modelAssetPath?: string;
  };
  /** Mirror the preview like a mirror (default true). */
  mirror?: boolean;
  /** Head occlusion (temples hidden behind the head and ears). Default true. */
  occlusion?: boolean;
  /**
   * Frame lock (default true): each camera frame is shown only once its
   * landmarks are known, so the frame sits on the face of the very image it
   * was tracked on — no prediction ahead of the video, no trail behind it.
   * Costs one inference of latency on the mirror (15–30 ms). Used only while
   * inference keeps up with the camera (quality high/medium); slower devices
   * show the live <video> with prediction instead.
   */
  frameLock?: boolean;
  /** Open the debug panel. */
  debug?: boolean;
  trackerMode?: 'auto' | 'worker' | 'inline';
  /** Skip the consent screen (host page already explained camera use). */
  skipConsent?: boolean;
  onClose?: () => void;
  onReady?: (info: { tracker: TrackerStatus; model: LoadReport }) => void;
}

/**
 * Orchestrates camera → tracker → pose solver → smoother → renderer, plus the
 * state machine, guidance, perf governor and debug panel.
 *
 * Two loops run concurrently:
 *  - the *inference* loop, driven by `requestVideoFrameCallback` (one grab per
 *    camera frame, skipped while the tracker is busy — back-pressure);
 *  - the *render* loop at display rate, drawing the predicted pose so the frame
 *    moves at 60 fps even when inference runs at 25–30 fps.
 */
export class TryOnApp {
  private readonly config: Required<Pick<TryOnConfig, 'mirror' | 'debug' | 'trackerMode' | 'skipConsent' | 'assetBase' | 'occlusion' | 'frameLock'>> & TryOnConfig;
  private readonly ui: OverlayElements;
  private readonly camera: CameraController;
  private readonly grabber: FrameGrabber;
  private readonly tracker: FaceTracker;
  private readonly solver: FramePoseSolver;
  private sizing!: SizingControls;
  private headSetup!: HeadSetup;
  private releaseFocus: (() => void) | null = null;
  /** A42E controls: pill, panels, desktop rail and column. */
  private readonly shell: ControlShell;
  private wearerPd: number | null = null;
  private readonly frameSwap = new FrameSwap();
  /** A201: the engraving on the 3D temples (rebuilt at most once per animation frame). */
  private readonly engrave3d = new TempleEngraving();
  private engrave3dPending = 0;
  private readonly smoother = new PoseSmoother();
  private readonly state = new TrackingStateMachine();
  private readonly perf = new PerfMonitor();
  private readonly trace = new TraceRecorder();
  private readonly governor = new QualityGovernor();
  /** A110W start-up choreography: tracker warm-up, reveal of the glasses, frame lock and governor arming. */
  private startupPhase = new StartupPhase();
  /** GPU warm-up: programs compiled, invisible draw done (see compileGpu / warmUpStep). */
  private warmCompiled = false;
  private warmDone = true;
  private loopFrames = 0;
  private renderer: Renderer | null = null;
  private frame: FrameObject | null = null;
  private frameScale = 1;
  private appearance: FrameAppearance | null = null;
  private debug: DebugPanel | null = null;
  /** A110T: Colours view of the current model, and « Voir le paysage » while it is open. */
  private materials: AppearanceControls | null = null;
  private lensView: LensView | null = null;
  private lensViewOpening = false;
  /** A110V « Je veux cette paire » (photo, specs, WhatsApp / photo / cart). */
  private order: OrderPanel | null = null;
  /**
   * A110Z temple engraving: one session for the whole try-on (kept across models), its desktop
   * column tab + temple band, and its full-screen phone view. Handmade models only.
   */
  private readonly engrave = new EngraveSession();
  private engravePanel: EngravePanel | null = null;
  private engraveBand: EngraveBand | null = null;
  private engraveScreen: EngraveScreen | null = null;
  private readonly engraveToast = document.createElement('p');
  private engraveToastTimer = 0;
  private engraveReturnFocus: (() => void) | null = null;
  private trackerReady: Promise<TrackerStatus> | null = null;
  private trackerStatus: TrackerStatus | null = null;

  private running = false;
  private closed = false;
  private starting = false;
  private rafId = 0;
  private bgTimer = 0;
  private rvfcId = 0;
  private grabbing = false;
  private frameCounter = 0;
  private lastDrawTs = -1;
  private lastDrawOpacity = -1;
  private lastDrawAt = -Infinity;
  private drawnFrames = 0;
  private firstVisibleAt: number | null = null;
  private startup = { cameraMs: 0, trackerMs: 0, frameMs: 0 };
  /** A110W phone measurement: display frames of the first 5 s after the camera image appears (stutters, worst gap). */
  private firstSeconds = { start: -1, last: -1, frames: 0, over50: 0, over100: 0, maxMs: 0, qualityChanges: 0, done: false };
  private measurementVisible = false;
  private visibilityInterruptions = 0;
  private physicalLensesWanted = true;
  private displayTimes: number[] = [];
  private lastVideoTime = -1;
  private openedAt = 0;
  private lastGovernorUpdate = 0;
  private lastUiUpdate = 0;
  private lastState: TrackingState = 'idle';
  /**
   * A110X: the try-on after the phone slept (hidden page, or a stall of
   * seconds as in the app switcher): wake-up cure, then a watchdog over the
   * camera, the tracker and the 3D (see EngineHealth).
   */
  private readonly health = new EngineHealth();
  private readonly levelHistory: LevelHistory;
  private hiddenAt = -1;
  private sleepBaseline = { fps: 0, inferMs: 0 };
  private lastTickAt = -1;
  private lastHealthCheck = 0;
  private framesSent = 0;
  private resultCount = 0;
  /** « Reprise de l’essayage… » instead of « Préparation… » until the glasses are back. */
  private resuming = false;
  private cameraRestarting = false;
  private trackerRestarting = false;
  private lastVideoCallbackAt = 0;
  private lastRvfcRearm = 0;
  /** A110Y: a camera frame arrived since the last render (the live background is uploaded only then). */
  private videoFrameNew = false;
  /** Bumped by each GPU warm-up launched: only the latest one marks the programs ready. */
  private gpuWarmToken = 0;
  private offContext: (() => void) | null = null;
  /** A110Y: listeners of the current session (camera, governor, tracker), removed before the next one. */
  private readonly sessionSubscriptions: Array<() => void> = [];
  /** A110Y: a frame is being built or switched on the main thread: a long gap is not a stall. */
  private building = 0;
  /** A201: the order sheet (PDF) is being drawn: long frames are ours (no stall, no watchdog), and a moment after. */
  private composing = 0;
  private composedAt = -Infinity;
  /** A110Y: automatic restarts of the camera / tracker, capped (see restartAllowed). */
  private readonly restartLog: number[] = [];
  private restartsCapped = false;
  /** A110Y: WebGL context losses in this session (two → « rafraîchir la page »). */
  private contextLosses = 0;
  /** A110Y: JS heap (Chrome only) at the opening, to spot a runaway session. */
  private heapAtOpen = 0;
  private memoryStepDownAt = 0;
  private lastMemoryCheck = 0;
  private grabWarnings = 0;
  /** A110Y: the recover message has been shown before in this session (its button then reloads the page). */
  private recoverShownBefore = false;
  /** A110Y: time of the last resume; results of frames grabbed before it are dropped. */
  private resumedAt = -Infinity;
  /** The session's own start-up gate, and when its loop started: a cold tracker is not judged in its first seconds. */
  private sessionPhase: StartupPhase | null = null;
  private sessionLoopAt = 0;

  private readonly solved: SolvedFramePose = createSolvedPose();
  /** Temple opening actually rendered (deg): ear-based estimate widened by the face mesh. */
  private templeSpreadDeg = 0;
  private lastSceneLightUpdate = 0;
  private readonly smoothed: SmoothedPose = { position: new Vector3(), quaternion: new Quaternion(), coasting: false, still: false, predictedMs: 0 };
  private hasSolved = false;
  private showLandmarks = false;
  private lastLandmarks: Float32Array | null = null;
  /** Jitter meters: projected anchor position over the last 60 render frames / 30 solver outputs. */
  private readonly jitterRing = new JitterMeter(60);
  private readonly rawJitterRing = new JitterMeter(30);
  /** Snapshot requests fulfilled right after the next real render (drawing buffer still intact). */
  private pendingSnapshots: Array<(snapshot: HTMLCanvasElement | null) => void> = [];
  /** Frame lock: ring of camera frames waiting for their landmarks (keyed by capture time). */
  private readonly lockRing: Array<{ captureTs: number; canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }> = [];
  private lockRingNext = 0;
  private videoCtx: CanvasRenderingContext2D | null = null;
  /** Frame lock active this frame (config on, quality keeps up, a frame has been presented). */
  private lockActive = false;
  /** Capture time of the frame currently shown on the video canvas (evaluation time of the pose while locked). */
  private lockShownTs = 0;
  /** A110Y: frame-lock hysteresis (see lockWanted / noteResultTiming). */
  private lockSteady = true;
  private onTimeResults = 0;
  /** Frame whose landmarks just arrived, to be painted together with its overlay on the next render. */
  private lockPending: { captureTs: number; canvas: HTMLCanvasElement } | null = null;

  constructor(container: HTMLElement, config: TryOnConfig) {
    this.config = {
      mirror: true,
      occlusion: true,
      frameLock: true,
      debug: false,
      trackerMode: 'auto',
      skipConsent: false,
      assetBase: defaultAssetBase(),
      ...config,
    };
    setLang(detectLang(config.lang));
    enableAnalyticsDebug(this.config.debug);

    const label = `${config.frame.model} — ${config.frame.colorway}`;
    this.ui = buildOverlay(container, label, this.config.mirror);
    this.camera = new CameraController(this.ui.video);
    this.grabber = new FrameGrabber(this.ui.video);
    this.solver = new FramePoseSolver(config.frame);
    this.shell = new ControlShell(this.ui.root, this.ui.stage, this.ui.helpBtn);
    this.setupEngraveToast();
    this.setupControls(config.frame);
    this.headSetup = new HeadSetup(this.ui.root, () => this.sizing.selection(),
      (pd, manual, face) => this.sizing.setCalibration(pd, manual, face),
      () => { if (this.running) this.shell.setReady(true); });
    this.order = new OrderPanel({
      stage: this.ui.stage,
      shop: detectShop(),
      dossierUrl: this.config.orderDossierUrl,
      retentionDays: this.config.dossierRetentionDays ?? 90,
      workshopRequired: () => Boolean(this.engravePanel),
      snapshot: () => {
        const snapshot = buildOrderSnapshot(this.config.frame, this.materials?.current() ?? null, this.sizing.selection(), this.engravePanel ? this.engrave.summary() : null);
        if (this.config.frame.id === config.frame.id && this.config.orderProductHandle) snapshot.handle = this.config.orderProductHandle;
        return snapshot;
      },
      capture: () => this.capturePhoto(),
      focus: () => this.photoFocus(),
      openEngrave: () => this.openEngrave(),
      engravingSheet: (family: string) => (this.engravePanel ? engravingSheet(this.engrave, family) : Promise.resolve(null)),
      workshopPage: (family: string, code: string) => (this.engravePanel ? workshopPage(this.engrave, family, code) : Promise.resolve(null)),
      busy: (on: boolean) => { this.composing = Math.max(0, this.composing + (on ? 1 : -1)); if (!on) this.composedAt = performance.now(); },
    });
    this.shell.setOrderView(this.order);
    const initialFrameId = config.frame.id;
    this.shell.setOrderHref(() => signatureProductHref(
      this.config.frame, initialFrameId, this.config.orderProductHandle,
      config.lang || document.documentElement.lang || 'en',
    ));
    // The order panel re-reads the engraving each time it is shown; a change while it is open refreshes it.
    this.engrave.onChange(event => {
      if (event === 'state') this.order?.refresh();
      if (event === 'state' || event === 'frame' || event === 'image') this.scheduleEngraving3d();
    });

    const base = this.config.assetBase.endsWith('/') ? this.config.assetBase : this.config.assetBase + '/';
    const a = this.config.assets ?? {};
    this.tracker = new FaceTracker({
      mode: this.config.trackerMode,
      wasmBasePath: new URL(`${base}wasm`, location.href).href,
      modelAssetPath: a.modelAssetPath ?? new URL(`${base}models/face_landmarker.task`, location.href).href,
      ...(a.wasmLoaderPath && a.wasmBinaryPath ? { wasmLoaderPath: a.wasmLoaderPath, wasmBinaryPath: a.wasmBinaryPath } : {}),
      ...(a.workerUrl ? { workerUrl: a.workerUrl } : {}),
    });

    this.ui.closeBtn.onclick = () => this.close();
    this.ui.cancelBtn.onclick = () => this.close();
    this.ui.allowBtn.onclick = () => void this.start();
    this.ui.retryBtn.onclick = () => void this.start();
    this.ui.recoverBtn.onclick = () => void this.relaunch();
    this.levelHistory = new LevelHistory(this.governor.levelIndex);
    this.installDebugTriggers();
  }

  /** A200: the lens setting a model opens with — « Original », or « Lunettes de vue » from an optical page. */
  private initialAppearance(spec: FrameSpec): AppearanceChoice {
    const base = defaultAppearance(spec);
    return lensOnly(spec) && this.config.opticalLenses ? { ...base, lens: CLEAR_LENS.id, gradient: false } : base;
  }

  /** Applies a non-« Original » opening setting to the frame on screen (the optical page's clear lenses). */
  private applyInitialAppearance(choice: AppearanceChoice): void {
    if (choice.lens === 'original' || !this.appearance) return;
    const look = this.appearance.apply(choice, this.renderer, this.lastDrawOpacity > 0.001 ? this.lastDrawOpacity : 0.5);
    this.solver.setFrameSpec({ ...this.solver.getFrameSpec(), look });
    this.lastDrawAt = -Infinity;
  }

  private setupControls(spec: FrameSpec, initial?: SizingSelection): void {
    const label = `${spec.model} — ${spec.colorway}`;
    this.ui.product.textContent = label;
    this.sizing = new SizingControls(spec, (mm) => {
      if (mm === this.wearerPd) return;
      this.wearerPd = mm;
      this.solver.setPupillaryDistance(mm);
      this.smoother.reset();
      this.renderer?.occluder.reset();
      this.hasSolved = false;
    }, (scale) => {
      this.frameScale = scale;
      const sized = sizeHandmade(spec, scale);
      this.solver.setFrameSpec({ ...sized, fit: this.solver.getFrameSpec().fit, look: this.solver.getFrameSpec().look });
      this.frame?.root.scale.setScalar(scale);
      this.updateTempleSpread();
      this.lastDrawAt = -Infinity;
    }, initial, () => this.shell.setKept(this.sizing.keptLabel()));
    let materials: AppearanceControls | null = null;
    const engravable = spec.line === 'Handmade' && Boolean(spec.customizable);
    if (lensOnly(spec)) {
      // A200 Signature: lenses only; the frame keeps its acetate and its title. A202: + the Sunset lenses (supplement).
      const initialChoice = this.initialAppearance(spec);
      materials = new AppearanceControls(choice => {
        if (!this.appearance) return;
        const look = this.appearance.apply(choice, this.renderer, this.lastDrawOpacity > 0.001 ? this.lastDrawOpacity : 0.5);
        this.solver.setFrameSpec({ ...this.solver.getFrameSpec(), look });
        this.lastDrawAt = -Infinity;
      }, false, initialChoice, { acetateColor: spec.look.acetateColor, lensColor: spec.look.lensColor },
      // A202: the Sunset lenses too, with « Voir le paysage » where the rear camera is offered.
      { lensOnly: true, ...(LensView.isSupported() ? { onLookThrough: () => void this.openLensView() } : {}) });
      this.applyInitialAppearance(initialChoice);
    } else if (engravable) {
      materials = new AppearanceControls(choice => {
        if (!this.appearance) return;
        const look = this.appearance.apply(choice, this.renderer, this.lastDrawOpacity > 0.001 ? this.lastDrawOpacity : 0.5);
        const name = acetateName(choice.acetate);
        // The colourway part of the title chip names the acetate being previewed.
        this.ui.product.textContent = name ? `${spec.model} — ${name}` : label;
        this.solver.setFrameSpec({ ...this.solver.getFrameSpec(), look });
        this.lastDrawAt = -Infinity;
        // A110Z: the temples of the engraving preview follow the acetate.
        this.engrave.setAcetate(choice.acetate, acetateSpec(choice.acetate)?.color ?? null);
      }, Boolean(spec.rearLaminate), defaultAppearance(spec), { acetateColor: spec.look.acetateColor, lensColor: spec.look.lensColor },
      { ...(LensView.isSupported() ? { onLookThrough: () => void this.openLensView() } : {}), onEngrave: () => this.openEngrave() });
    }
    this.materials = materials;
    this.setupEngrave(spec, engravable, materials?.current().acetate ?? 'original');
    this.shell.setSizeView(this.sizing.root);
    this.shell.setColoursView(materials?.root ?? null);
    this.shell.setLaminated(Boolean(spec.rearLaminate));
    this.shell.setKept(this.sizing.keptLabel());
    this.order?.refresh();
  }

  /**
   * A110Z: the engraving follows the model. The views exist once (the session outlives models);
   * a model without material preview (Signature) removes them. A custom engraving kept across a
   * model change is announced (« Gravure conservée sur {Modèle} »).
   */
  private setupEngrave(spec: FrameSpec, engravable: boolean, acetate: string): void {
    if (!engravable) {
      this.engraveScreen?.close();
      this.shell.setEngraveView(null);
      this.engravePanel?.destroy(); this.engravePanel = null;
      this.engraveBand?.destroy(); this.engraveBand = null;
      this.scheduleEngraving3d();
      return;
    }
    const kept = this.engrave.setFrame({ id: spec.id, model: spec.model, colorway: spec.colorway, acetateColor: spec.look.acetateColor ?? null });
    this.engrave.setAcetate(acetate, acetateSpec(acetate)?.color ?? null);
    if (!this.engravePanel) {
      this.engravePanel = new EngravePanel(this.engrave);
      this.engraveBand = new EngraveBand(this.engrave);
      const panel = this.engravePanel, band = this.engraveBand;
      this.shell.setEngraveView({ root: panel.root, band: band.root, onShow: () => { panel.onShow(); band.setVisible(true); }, onHide: () => { panel.onHide(); band.setVisible(false); } });
    }
    if (kept) this.showEngraveToast(words().engrave.kept(spec.model));
  }

  /** A201: the 3D engraving follows the session (texts, colours, sizes, places), the model and its temple photo. */
  private scheduleEngraving3d(): void {
    if (this.engrave3dPending) return;
    this.engrave3dPending = requestAnimationFrame(() => {
      this.engrave3dPending = 0;
      this.refreshEngraving3d();
    });
  }

  private refreshEngraving3d(): void {
    const frame = this.frame;
    if (this.closed || !frame || !this.engravePanel || frame.spec.id !== this.engrave.frame.id) { this.engrave3d.update(null, null, false); return; }
    const facts = this.engrave.facts();
    this.engrave3d.update(frame, { state: this.engrave.state, font: this.engrave.font, places: facts.placesMm ?? {}, caps: facts.capsMm ?? {} }, this.config.mirror);
  }

  private setupEngraveToast(): void {
    this.engraveToast.className = 'mt-eng-toast';
    this.engraveToast.setAttribute('role', 'status');
    this.engraveToast.hidden = true;
    this.ui.stage.append(this.engraveToast);
  }

  private showEngraveToast(text: string): void {
    window.clearTimeout(this.engraveToastTimer);
    this.engraveToast.textContent = '';
    const pen = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    pen.setAttribute('viewBox', '0 0 24 24'); pen.setAttribute('width', '14'); pen.setAttribute('height', '14');
    pen.setAttribute('fill', 'none'); pen.setAttribute('stroke', 'currentColor'); pen.setAttribute('stroke-width', '1.7'); pen.setAttribute('stroke-linecap', 'round'); pen.setAttribute('stroke-linejoin', 'round');
    pen.setAttribute('aria-hidden', 'true'); pen.classList.add('mt-icon');
    pen.innerHTML = '<path d="M4 20l4.2-1 10.3-10.3a1.8 1.8 0 0 0 0-2.5l-.7-.7a1.8 1.8 0 0 0-2.5 0L5 15.8z"/><path d="M13.5 7.2l3.3 3.3"/>';
    this.engraveToast.append(pen, document.createTextNode(text));
    this.engraveToast.hidden = false;
    this.engraveToastTimer = window.setTimeout(() => { this.engraveToast.hidden = true; }, 2600);
  }

  /** Opens the engraving: the column tab on desktop, the full-screen view on phones. */
  openEngrave(): void {
    if (!this.engravePanel) return;
    if (this.shell.isDesktop) { this.shell.open('engrave'); return; }
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!this.engraveScreen) {
      this.engraveScreen = new EngraveScreen(this.engrave, { stage: this.ui.stage, onClose: () => { this.order?.refresh(); this.engraveReturnFocus?.(); } });
    }
    this.engraveReturnFocus = () => { this.engraveReturnFocus = null; if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true }); };
    this.shell.open(null);
    this.engraveScreen.open();
    track('tryon_engrave_open', { product_handle: this.config.frame.productHandle, frame_id: this.config.frame.id });
  }

  /** A42E: the model list (rail on desktop, panel on mobile), supplied by the embed. */
  setFramesView(view: FramesViewHandle | null): void {
    this.shell.setFramesView(view);
    this.shell.setKept(this.sizing.keptLabel());
  }

  /** Replace only the model; camera, worker, renderer and PD calibration survive. */
  async switchFrame(spec: FrameSpec, resolveSpec?: () => Promise<FrameSpec>): Promise<boolean> {
    if (this.closed || !this.running || !this.renderer) return false;
    const renderer = this.renderer;
    return this.frameSwap.replace(async () => {
      if (resolveSpec) spec = await resolveSpec();
      // A110Y: the procedural build blocks the main thread for 1–6 s on a phone; that gap must
      // not be taken for a sleep (wakeUp 'stall' restarted the start-up phase, even the tracker).
      this.building++;
      let next: FrameObject;
      try {
        next = (await loadFrame(spec, renderer.gl, this.config.assetBase)).frame;
      } finally {
        this.building--;
        this.lastTickAt = performance.now();
      }
      // A110W: compiled and uploaded while the current model is still shown.
      try {
        if (!this.closed) await prepareFrame(renderer, next, this.lastDrawOpacity, () => this.closed || this.renderer !== renderer);
      } catch {
        /* compiled on first draw instead */
      }
      return next;
    }, next => {
      const previous = this.frame;
      const sizing = this.sizing.selection();
      this.appearance?.dispose(); this.appearance = null;
      this.frame = next;
      this.config.frame = spec;
      this.frameScale = 1;
      this.solver.setFrameSpec(spec);
      if (hasAppearance(spec)) this.appearance = new FrameAppearance(this.frame);
      this.setupControls(spec, sizing);
      renderer.setFrame(this.frame);
      this.scheduleEngraving3d();
      previous?.dispose();
      this.firstVisibleAt = null;
      this.lastDrawAt = -Infinity;
      this.order?.frameChanged();
    });
  }

  /** Shows the modal. The tracking engine starts loading immediately, before the camera consent. */
  async open(): Promise<void> {
    this.releaseFocus = modalFocus(this.ui.root, () => { if (!this.shell.dismiss()) this.close(); });
    this.openedAt = performance.now();
    track('tryon_open', { product_handle: this.config.frame.productHandle, frame_id: this.config.frame.id });
    this.trackerReady = this.tracker.init();
    this.trackerReady.catch(() => undefined); // surfaced in start()

    let consented = this.config.skipConsent;
    try {
      consented ||= sessionStorage.getItem('mata_tryon_consent') === '1';
    } catch {
      /* storage unavailable */
    }
    if (consented) await this.start();
    else this.ui.consent.hidden = false;
  }

  /** A110Y: removes the listeners registered by the previous startSession(). */
  private unsubscribeSession(): void {
    for (const off of this.sessionSubscriptions) {
      try {
        off();
      } catch {
        /* ignore */
      }
    }
    this.sessionSubscriptions.length = 0;
  }

  private async start(): Promise<void> {
    if (this.closed || this.starting || this.running) return;
    this.starting = true;
    try {
      await this.startSession();
    } catch (err) {
      this.camera.stop();
      if (!this.closed) {
        track('tryon_error', { code: 'startup', message: String(err) });
        showError(this.ui, 'error_engine');
      }
    } finally {
      this.starting = false;
      if (!this.running) this.headSetup.hide();
    }
  }

  private async startSession(): Promise<void> {
    const specReady = this.config.loadFrameSpec?.();
    specReady?.catch(() => undefined);
    this.ui.consent.hidden = true;
    this.ui.error.hidden = true;
    this.ui.loading.hidden = false;
    // A110W: the governor waits for meaningful measurements (see StartupPhase).
    this.startupPhase = new StartupPhase();
    this.sessionPhase = this.startupPhase;
    this.governor.hold();
    this.warmCompiled = false;
    this.warmDone = false;
    this.loopFrames = 0;
    this.firstSeconds = { start: -1, last: -1, frames: 0, over50: 0, over100: 0, maxMs: 0, qualityChanges: 0, done: false };

    const cameraStart = performance.now();
    let size: { width: number; height: number };
    try {
      size = await this.camera.start();
      this.startup.cameraMs = performance.now() - cameraStart;
      if (this.closed) return;
      track('tryon_camera_granted');
      this.headSetup.show();
      try {
        sessionStorage.setItem('mata_tryon_consent', '1');
      } catch {
        /* ignore */
      }
    } catch (err) {
      if (this.closed) return;
      const code = err instanceof CameraError ? err.code : 'unknown';
      track('tryon_camera_denied', { code });
      const key: StringKey =
        code === 'denied' ? 'error_denied' : code === 'no_device' ? 'error_no_device' : code === 'insecure_context' ? 'error_insecure' : code === 'in_use' ? 'error_in_use' : 'error_unsupported';
      showError(this.ui, key, code !== 'insecure_context');
      return;
    }

    try {
      this.trackerStatus = await (this.trackerReady ?? this.tracker.init());
      this.startup.trackerMs = this.trackerStatus.initMs;
    } catch (err) {
      if (this.closed) return;
      this.trackerReady = null;
      this.camera.stop();
      track('tryon_error', { code: 'engine', message: String(err) });
      showError(this.ui, 'error_engine');
      return;
    }

    if (this.closed) return;
    if (!this.renderer) {
      // A110Y: a refused context (17th context of the page, graphics memory exhausted) gets its
      // own message instead of « le moteur n’a pas pu démarrer ».
      let creationError: string | null = null;
      const onCreationError = (e: Event): void => { creationError = (e as WebGLContextEvent).statusMessage || 'context creation failed'; };
      this.ui.canvas.addEventListener('webglcontextcreationerror', onCreationError, false);
      try {
        this.renderer = new Renderer(this.ui.canvas);
      } catch (err) {
        this.camera.stop();
        track('tryon_error', { code: 'webgl', message: creationError ?? String(err) });
        showError(this.ui, 'error_graphics');
        return;
      } finally {
        this.ui.canvas.removeEventListener('webglcontextcreationerror', onCreationError, false);
      }
      this.offContext = this.renderer.onContextChange((lost) => this.onContextChange(lost));
      this.renderer.physicalLenses = this.physicalLensesWanted;
      this.renderer.occluder.setParams({ enabled: this.config.occlusion });
    }
    const renderer = this.renderer;
    this.applyVideoSize(size.width, size.height);
    // A110Y: one set of listeners per session. Before, « Réessayer » after a tracker error ran
    // startSession() again and every listener was registered twice (each result handled twice).
    this.unsubscribeSession();
    this.sessionSubscriptions.push(this.camera.onResize((w, h) => this.applyVideoSize(w, h)));
    this.applyQuality(this.governor.current, 'initial');
    this.sessionSubscriptions.push(this.governor.onChange((level, reason) => {
      this.levelHistory.record(performance.now(), this.governor.levelIndex);
      this.applyQuality(level, reason);
      if (!this.firstSeconds.done) this.firstSeconds.qualityChanges++;
      track('tryon_quality_change', { quality_level: level.name, reason });
    }));

    // A110W: the worker already paid the tracker's cold start on a blank frame
    // (face.worker.ts); the first camera inferences (the landmark model's own
    // first run) proceed while the frame is finished, in the background.
    void this.warmTracker();

    let report: LoadReport;
    try {
      const loaded = await this.loadSessionFrame(specReady, renderer);
      if (this.closed) { loaded.frame.dispose(); return; }
      const previous = this.frame;
      this.appearance?.dispose();
      this.appearance = null;
      this.frame = loaded.frame;
      if (hasAppearance(this.config.frame)) this.appearance = new FrameAppearance(this.frame);
      // A200: a lens chosen before the frame was ready (or the optical page's clear lenses).
      const opening = this.materials?.current();
      if (opening) this.applyInitialAppearance(opening);
      this.frame.root.scale.setScalar(this.frameScale);
      report = loaded.report;
      renderer.setFrame(this.frame);
      this.scheduleEngraving3d();
      if (previous && previous !== this.frame) previous.dispose();
      for (const w of report.warnings) console.warn('[tryon:model]', w);
    } catch (err) {
      if (this.closed) return;
      this.camera.stop();
      track('tryon_error', { code: 'model', message: String(err) });
      showError(this.ui, 'error_engine');
      return;
    }

    // A110W: the photos go to the GPU now, behind the loading screen (their
    // decode and upload would otherwise stall a frame later); the shader
    // programs compile in the background while the camera image is already
    // shown, and the glasses wait for them (see StartupPhase).
    try {
      uploadTextures(renderer, this.frame.root);
    } catch {
      /* uploaded on first draw instead */
    }
    void this.compileGpu();

    this.ui.loading.hidden = true;
    this.shell.setReady(this.headSetup.complete);
    this.headSetup.engineReady();
    this.sessionSubscriptions.push(this.tracker.onResult((r) => this.onResult(r)));
    this.sessionSubscriptions.push(this.tracker.onError((message, fatal) => {
      console.error('[tryon:tracker]', message);
      if (fatal) {
        this.shell.setReady(false);
        track('tryon_error', { code: 'tracker', message });
        showError(this.ui, 'error_engine');
        this.stopLoops();
        this.camera.stop();
        // A110Y: « Réessayer » must get a new tracker, not the one that just failed.
        this.trackerReady = null;
      }
    }));

    if (this.config.debug) this.openDebug();
    track('tryon_engine_ready', {
      tracker_mode: this.trackerStatus.mode,
      delegate: this.trackerStatus.delegate,
      init_ms: Math.round(this.trackerStatus.initMs),
      quality_level: this.governor.current.name,
    });
    this.config.onReady?.({ tracker: this.trackerStatus, model: report });

    this.running = true;
    this.sessionLoopAt = performance.now();
    this.state.start(performance.now());
    this.scheduleGrab();
    this.rafId = requestAnimationFrame((t) => this.renderLoop(t));
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pageshow', this.onPageShow);
    if (document.visibilityState !== 'visible') this.onVisibility();
  }

  /** Resolves the catalogue entry and builds the frame (procedural build on the main thread, then its photos). */
  private async loadSessionFrame(specReady: Promise<FrameSpec> | undefined, renderer: Renderer): Promise<{ frame: FrameObject; report: LoadReport }> {
    const frameStart = performance.now();
    if (specReady) {
      const full = await specReady;
      if (this.closed) throw new Error('Try-on closed');
      const selection = this.sizing.selection();
      this.config.frame = full;
      this.solver.setFrameSpec(full);
      this.setupControls(full, selection);
    }
    this.building++;
    try {
      const loaded = await loadFrame(this.config.frame, renderer.gl, this.config.assetBase.endsWith('/') ? this.config.assetBase : this.config.assetBase + '/');
      this.startup.frameMs = performance.now() - frameStart;
      return loaded;
    } finally {
      this.building--;
      this.lastTickAt = performance.now();
    }
  }

  /**
   * A110W GPU warm-up, part 1 (see ShaderWarmup): every program of the scene
   * compiled in the state of the first frame with the glasses (fading in),
   * polled without blocking. Part 2, one invisible draw, runs in the render
   * loop (tick). Bounded, never fails the start.
   */
  private async compileGpu(): Promise<void> {
    const renderer = this.renderer;
    const frame = this.frame;
    // A110X: also run after a restored WebGL context; only the latest warm-up counts.
    const token = ++this.gpuWarmToken;
    try {
      if (renderer && frame) {
        frame.setOpacity(WARM_FADE_OPACITY);
        await compilePrograms(renderer, renderer.scene, { stop: () => this.closed || this.renderer !== renderer });
      }
    } catch (err) {
      console.warn('[tryon:warmup]', err);
    }
    if (token !== this.gpuWarmToken) return;
    this.warmCompiled = true;
    if (!renderer || !frame || renderer !== this.renderer || frame !== this.frame) {
      // Replaced meanwhile (frame switch, which prepares its own model): nothing left to warm.
      this.warmDone = true;
      this.startupPhase.programsReady(performance.now());
    }
  }

  /**
   * A110W GPU warm-up, part 2 (render loop, glasses still hidden): once the
   * programs are compiled, one full draw with the glasses posed in front of
   * the camera (shadow pass program, lens composite path), right before this
   * frame's real draw in the same task: never presented. Returns true when
   * this frame carried that draw.
   */
  private warmUpStep(): boolean {
    // The very first frame already compiles the camera background: leave it alone.
    if (this.warmDone || !this.warmCompiled || this.loopFrames < 2) return false;
    this.warmDrawGpu();
    return true;
  }

  private warmDrawGpu(): void {
    this.warmDone = true;
    const renderer = this.renderer;
    try {
      if (renderer) warmDraw(renderer, this.solver.getFrameSpec(), null, WARM_FADE_OPACITY);
    } catch (err) {
      console.warn('[tryon:warmup]', err);
    }
    this.startupPhase.programsReady(performance.now());
  }

  /**
   * A110W tracker warm-up: the first inferences of a session are the slow ones
   * (wasm tier-up, XNNPACK weight packing, GPU-delegate shaders — the first can
   * take many times the steady duration). After the worker's own blank-frame
   * warm-up, the first camera frames are inferred while the frame is being
   * finished (photos, programs), until the render loop takes over, so it
   * starts with a tracker at its steady pace. Results only feed the warm-up
   * statistics.
   */
  private async warmTracker(): Promise<void> {
    // Inline mode infers on this very thread: one inference pays the cold start, more would only delay.
    const count = this.trackerStatus?.mode === 'inline' ? 1 : 6;
    for (let i = 0; i < count && !this.running && !this.closed && !this.startupPhase.trackerWarm; i++) {
      if (!(await this.inferOnce())) return;
    }
  }

  /** One warm-up inference on the current camera frame. */
  private inferOnce(): Promise<TrackerResult | null> {
    return new Promise((resolve) => {
      let settled = false;
      const done = (result: TrackerResult | null) => {
        if (settled) return;
        settled = true;
        offResult();
        offError();
        clearTimeout(timer);
        resolve(result);
      };
      const offResult = this.tracker.onResult((r) => {
        // Once the loop runs, onResult() accounts for this result itself.
        if (!this.running) this.startupPhase.inference(r.inferMs, !!r.landmarks, performance.now());
        done(r);
      });
      const offError = this.tracker.onError(() => done(null));
      const timer = window.setTimeout(() => done(null), 10_000);
      if (this.tracker.busy || this.ui.video.videoWidth === 0) { done(null); return; }
      const ts = performance.now();
      if (this.trackerStatus?.mode === 'inline') {
        if (!this.tracker.detect(this.ui.video, ts, ts)) done(null);
        return;
      }
      this.grabber
        .grab(this.ui.video)
        .then((bitmap) => {
          if (!bitmap) { done(null); return; }
          if (this.closed) { bitmap.close(); done(null); return; }
          if (!this.tracker.detect(bitmap, ts, ts)) done(null);
        })
        .catch(() => done(null));
    });
  }

  private applyVideoSize(width: number, height: number): void {
    // A110Y: a track that reports 0×0 while the phone wakes up must not zero the intrinsics.
    if (!(width > 0) || !(height > 0)) return;
    this.solver.setVideoSize(width, height);
    const k = this.solver.getIntrinsics();
    if (k && this.renderer) this.renderer.setVideo(width, height, k);
    this.lastDrawAt = -Infinity;
    // A110Y: the landmark canvas is sized only when it is drawn (debug panel): a blank
    // full-resolution canvas on every customer's page before.
    if (this.showLandmarks || this.debug) {
      this.ui.debugCanvas.width = width;
      this.ui.debugCanvas.height = height;
    }
  }

  private edgeAATest = false;

  private applyQuality(level: QualityLevel, reason: string): void {
    this.grabber.targetLongSide = level.inferLongSide;
    const dpr = Math.min(window.devicePixelRatio || 1, level.pixelRatioCap);
    this.renderer?.setPixelRatio(this.edgeAATest ? Math.max(1.5, dpr) : dpr);
    this.renderer?.setShadowMapSize(level.shadowMapSize);
    // A resized canvas is blank until drawn again: the governor runs before the
    // render of the current frame, which must then draw (frame lock or not).
    this.lastDrawAt = -Infinity;
    // Start-up window: the next verdict judges the new level on its own frames.
    if (reason !== 'initial' && this.governor.inStartupWindow(performance.now())) this.perf.resetTiming();
    if (this.config.debug) console.info(`[tryon:quality] ${level.name} (${reason})`);
  }

  // ---------------------------------------------------------------- inference loop

  private scheduleGrab(): void {
    const video = this.ui.video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
    };
    if (typeof video.requestVideoFrameCallback === 'function') {
      this.rvfcId = video.requestVideoFrameCallback(() => {
        if (!this.running) return;
        this.lastVideoCallbackAt = performance.now();
        this.videoFrameNew = true;
        this.onVideoFrame();
        this.scheduleGrab();
      });
    }
  }

  private onVideoFrame(): void {
    this.frameCounter++;
    if (this.frameCounter % this.governor.current.inferEveryNthFrame !== 0) return;
    if (this.tracker.busy || this.grabbing || this.trackerRestarting) return; // back-pressure: keep only the freshest frame
    const captureTs = performance.now();
    // A110X watchdog: frames offered to an idle tracker (a tracker that never answers them is restarted).
    this.framesSent++;

    if (this.trackerStatus?.mode === 'inline') {
      // Inline mode: hand the <video> element directly (no bitmap copy).
      this.tracker.detect(this.ui.video, captureTs, captureTs);
      return;
    }

    this.grabbing = true;
    const captured = this.lockWanted() ? this.lockStore(captureTs) : null;
    this.grabber
      .grab(captured ?? this.ui.video)
      .then((bitmap) => {
        this.grabbing = false;
        if (!bitmap) return;
        if (!this.running) {
          bitmap.close();
          return;
        }
        this.tracker.detect(bitmap, captureTs, captureTs);
      })
      .catch((err) => {
        this.grabbing = false;
        // A110Y: a camera that fails to grab durably would log 30 warnings per second.
        if (++this.grabWarnings <= 3) console.warn('[tryon:grab]', err);
        else if (this.grabWarnings === 4) console.warn('[tryon:grab] further grab errors are not logged');
      });
  }

  // ---------------------------------------------------------------- frame lock

  /** Frame lock wanted now: configured, worker mode, and inference keeping up with the camera. */
  private lockWanted(): boolean {
    if (!this.config.frameLock || this.trackerStatus?.mode !== 'worker') return false;
    // A110W: before that, the display would be paced by cold, irregular inferences.
    if (!this.startupPhase.lockAllowed) return false;
    // A110Y: at every quality level. In « live video » mode (the old low/minimal levels) the glasses
    // are predicted to « now » from samples 70–100 ms apart on a slow phone: 5–9 px of vibration in
    // motion, 12–45 px jumps (the « jelly » look). Frame lock at 12 fps shows a slower mirror
    // image, but the glasses stay glued to the face (0.4 px). Hysteresis: the lock is left on a
    // late result and taken again after three results on time.
    return this.lockSteady && !document.hidden;
  }

  /** A110Y: inference cadence (ms between results), from the perf monitor, floored at 33 ms. */
  private inferIntervalMs(): number {
    const perSecond = this.perf.snapshot().inferPerSecond;
    return perSecond > 0 ? Math.max(33, 1000 / perSecond) : 66;
  }

  /** A110Y: a result far later than the cadence leaves the lock; three results on time take it again. */
  private noteResultTiming(r: TrackerResult, now: number): void {
    const late = now - r.captureTs > Math.max(300, 3 * this.inferIntervalMs());
    if (late) {
      this.lockSteady = false;
      this.onTimeResults = 0;
    } else if (!this.lockSteady && ++this.onTimeResults >= 3) {
      this.lockSteady = true;
    }
  }

  /** Copies the current video frame into the ring, tagged with its capture time. */
  private lockStore(captureTs: number): HTMLCanvasElement | null {
    const video = this.ui.video;
    if (video.videoWidth === 0) return null;
    if (this.lockRing.length === 0) {
      for (let i = 0; i < 3; i++) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return null;
        this.lockRing.push({ captureTs: -1, canvas, ctx });
      }
    }
    const slot = this.lockRing[this.lockRingNext]!;
    this.lockRingNext = (this.lockRingNext + 1) % this.lockRing.length;
    if (slot.canvas.width !== video.videoWidth || slot.canvas.height !== video.videoHeight) {
      slot.canvas.width = video.videoWidth;
      slot.canvas.height = video.videoHeight;
    }
    try {
      slot.ctx.drawImage(video, 0, 0);
      slot.captureTs = captureTs;
      return slot.canvas;
    } catch {
      slot.captureTs = -1;
      return null;
    }
  }

  /** Queues the stored frame whose landmarks just arrived; leaves the lock when nothing was stored. */
  private lockPresent(captureTs: number): void {
    const slot = this.lockRing.find((s) => s.captureTs === captureTs);
    if (!slot || !this.lockWanted()) {
      this.lockPending = null;
      this.lockLeave();
      return;
    }
    slot.captureTs = -1;
    this.lockPending = { captureTs, canvas: slot.canvas };
  }

  /** Paints the queued frame — same task as the overlay render that follows, so both change in the same display refresh. */
  private lockPaint(): void {
    const pending = this.lockPending;
    if (!pending) return;
    this.lockPending = null;
    const target = this.ui.videoCanvas;
    if (target.width !== pending.canvas.width || target.height !== pending.canvas.height) {
      target.width = pending.canvas.width;
      target.height = pending.canvas.height;
      this.videoCtx = null;
    }
    this.videoCtx ??= target.getContext('2d', { alpha: false });
    if (!this.videoCtx) return;
    this.videoCtx.drawImage(pending.canvas, 0, 0);
    this.lockShownTs = pending.captureTs;
    const shownAt = performance.now();
    this.displayTimes.push(shownAt);
    while (this.displayTimes.length && shownAt - this.displayTimes[0]! > 1000) this.displayTimes.shift();
    if (!this.lockActive) {
      this.lockActive = true;
      target.hidden = false;
      this.ui.video.classList.add('mt-video--locked');
    }
  }

  private lockLeave(): void {
    if (!this.lockActive) return;
    this.lockActive = false;
    this.ui.videoCanvas.hidden = true;
    this.ui.video.classList.remove('mt-video--locked');
  }

  private onResult(r: TrackerResult): void {
    if (!this.running) return;
    this.resultCount++;
    // A110Y: a frame grabbed before the resume (wake-up, restarted camera) is a pose of before it.
    if (r.captureTs < this.resumedAt) return;
    const now = performance.now();
    this.noteResultTiming(r, now);
    if (this.startupPhase.inference(r.inferMs, !!r.landmarks, now)) this.perf.resetInference();
    if (this.startupPhase.trackerWarm) this.perf.markInference(r.inferMs, now - r.captureTs, now);
    this.trace.push(r, now);
    const k = this.solver.getIntrinsics();
    this.lockPresent(r.captureTs);

    let poseReady = false;
    let irisPx = 0;
    let faceWidthRatio = 0;
    if (r.landmarks && k) {
      this.lastLandmarks = r.landmarks;
      const tSeconds = r.captureTs / 1000; // pose is stamped at capture time, not at result time
      const ok = this.solver.solve({ landmarks: r.landmarks, matrix: r.matrix, width: r.width, height: r.height }, tSeconds, this.solved);
      if (ok) {
        poseReady = true;
        this.hasSolved = true;
        irisPx = this.solved.iris.meanPx;
        faceWidthRatio = this.solved.bounds.w / Math.min(k.width, k.height);
        const raw = project(k, this.solved.position);
        this.rawJitterRing.push(raw.u, raw.v);
      } else {
        irisPx = measureIris(r.landmarks, k.width, k.height).meanPx;
      }
    } else {
      this.lastLandmarks = null;
    }

    const next = this.state.update({
      faceDetected: !!r.landmarks,
      luma: this.grabber.luma(),
      faceWidthRatio,
      irisPx,
      faceCount: r.faceCount,
      poseReady,
      nowMs: now,
    });
    if (poseReady && r.landmarks && k) {
        if (this.state.consumeResetFlag()) {
          this.smoother.reset();
          this.renderer?.occluder.reset();
        }
        this.smoother.push({ position: this.solved.position, quaternion: this.solved.quaternion, tSeconds: r.captureTs / 1000 });
        this.renderer?.occluder.updateFromLandmarks(r.landmarks, k, this.solved);
        this.updateTempleSpread();
    }
    this.onStateChange(next);
  }

  /**
   * Temples open enough to clear the wearer's head as seen by the face mesh
   * (a 150 mm frame on a 150 mm head must flex outwards, like real acetate),
   * never less than the ear-based estimate. Smoothed (A110Y: symmetrically).
   */
  private updateTempleSpread(): void {
    const occ = this.renderer?.occluder;
    const frame = this.frame;
    if (!occ || !frame) return;
    const spec = this.solver.getFrameSpec();
    // Hinge axis as built (procedural end-piece block or GLB hinge node), frame space.
    const hinge = frame.hingeL?.position;
    const scale = frame.root.scale.x;
    const hingeX = hinge ? hinge.x * scale : spec.dims.widthMm / 2 - 1.5;
    const hingeY = hinge ? hinge.y * scale : spec.silhouette?.hingeY ?? spec.dims.lensHeightMm / 2 + 3.5 - 4.5;
    const hingeZ = hinge ? hinge.z * scale : -((hingeX * hingeX) / 500) + 0.5;
    const templeWidth = spec.temple?.widthMm ?? 3.6 * scale;
    const centreX = spec.temple && spec.silhouette
      ? Math.max(...spec.silhouette.outer.map(([x]) => x)) - spec.temple.outerInsetMm - templeWidth / 2
      : hingeX - scale;
    const needed = occ.requiredTempleSpreadDeg(hingeX, hingeY, hingeZ, centreX, templeWidth / 2 + 1);
    const target = Math.max(-3, Math.min(14, Math.max(this.solved.templeSpreadDeg, needed ?? -Infinity)));
    // A110Y: symmetric smoothing. The old 0.35 / 0.08 pair was a ratchet on the mesh noise: the
    // temples opened on every noise peak and closed slowly — a quiver at the ears.
    const alpha = 0.15;
    this.templeSpreadDeg += (target - this.templeSpreadDeg) * alpha;
  }

  private onStateChange(next: TrackingState): void {
    if (next === this.lastState) return;
    if (next === 'tracking' && (this.lastState === 'acquiring' || this.lastState === 'lost' || this.lastState === 'searching')) {
      track('tryon_tracking_acquired', { reacquire_ms: Math.round(this.state.stats.lastReacquireMs) });
    } else if (next === 'lost') {
      track('tryon_tracking_lost', { losses: this.state.stats.losses });
    }
    this.lastState = next;
  }

  // ---------------------------------------------------------------- render loop

  private renderLoop(now: number): void {
    if (!this.running) return;
    this.rafId = requestAnimationFrame((t) => this.renderLoop(t));
    this.tick(now);
  }

  /**
   * Background mode: browsers freeze requestAnimationFrame in hidden tabs, so
   * while the page is hidden a (throttled, ~1 Hz) timer keeps grabbing frames
   * and rendering. Not for the user (they cannot see the page) but for remote
   * QA captures taken while the tester is looking at another window.
   */
  private startBackgroundTicks(): void {
    if (this.bgTimer !== 0) return;
    this.bgTimer = window.setInterval(() => {
      if (!this.running || document.visibilityState === 'visible') return;
      this.onVideoFrame();
      this.tick(performance.now());
    }, 250);
  }

  private stopBackgroundTicks(): void {
    if (this.bgTimer === 0) return;
    clearInterval(this.bgTimer);
    this.bgTimer = 0;
    // The 1 Hz background intervals must not count as the device's frame rate.
    this.perf.resetTiming();
  }

  /** One frame of the render loop (state, smoothing, render, snapshots, UI). */
  private tick(now: number): void {
    // A110X: a visible page that did not draw for seconds (app switcher, a
    // phone that froze the page without hiding it) wakes up like a hidden one.
    // A110Y: not while a frame is being built (that gap is ours, see switchFrame).
    if (!document.hidden && this.hiddenAt < 0 && this.building === 0 && !this.composingNow(now) && this.lastTickAt >= 0 && now - this.lastTickAt > STALL_MS) {
      this.sleepBaseline = { fps: this.perf.sessionAverageFps, inferMs: this.perf.inferMs };
      this.wakeUp(this.lastTickAt, now - this.lastTickAt, 'stall');
    }
    if (!document.hidden) this.lastTickAt = now;
    if (now - this.lastMemoryCheck > 5000) {
      this.lastMemoryCheck = now;
      this.checkMemory(now);
    }
    this.perf.markRender(now);
    this.loopFrames++;
    this.startupPhase.loopStarted(now);
    this.countFirstSeconds(now);
    // Before drawing: a quality change resizes the canvas, redrawn in this same task.
    this.updateGovernor(now);

    // Fallback frame detection when requestVideoFrameCallback is unavailable.
    if (this.rvfcId === 0) {
      const ct = this.ui.video.currentTime;
      if (ct !== this.lastVideoTime) {
        this.lastVideoTime = ct;
        this.videoFrameNew = true;
        this.onVideoFrame();
      }
    }

    const state = this.state.tick(now);
    this.onStateChange(state);
    // A110W: the glasses stay hidden until programs, tracker and tracking are
    // steady, then fade in (never a stuttering first appearance).
    this.startupPhase.update(now, state === 'tracking');
    if (this.resuming && this.startupPhase.revealed) this.resuming = false;
    const opacity = this.startupPhase.gate(this.state.opacity(now), now);
    if (!document.hidden) this.watch(now);
    // Frame lock: the frame whose landmarks arrived is painted now, and the
    // pose is evaluated at its capture time (no prediction — the image is not
    // ahead of the landmarks; the filter trail is still compensated).
    // Otherwise the live <video> shows and the pose is predicted to "now".
    // A slow phone (12 fps) must not drop the lock over two missed frames: the limit follows the cadence.
    if (!this.lockWanted() || (this.lockActive && now - this.lockShownTs > Math.max(300, 3 * this.inferIntervalMs()))) this.lockLeave();
    const imageChanged = this.lockPending !== null;
    this.lockPaint();
    this.renderer?.setCameraFrame(this.lockActive ? this.ui.videoCanvas : this.ui.video, this.lockActive ? imageChanged : this.videoFrameNew);
    this.videoFrameNew = false;
    const evalMs = this.lockActive ? this.lockShownTs : now;
    const hasPose = this.hasSolved && this.smoother.evaluate(evalMs / 1000, this.smoothed, true);

    // A 120 Hz screen must not redraw the same locked camera image four times.
    // UI/state timers still tick; fades and explicit captures always draw.
    // A110W: GPU warm-up steps (uploads, then a draw right before this frame's real draw).
    const warmUp = this.warmUpStep();
    const draw = warmUp || !this.lockActive || this.lockShownTs !== this.lastDrawTs || opacity !== this.lastDrawOpacity || this.pendingSnapshots.length > 0 || now - this.lastDrawAt > 100;
    if (draw) {
      this.renderer?.render(
      hasPose ? this.smoothed.position : null,
      hasPose ? this.smoothed.quaternion : null,
      this.solver.getFrameSpec(),
      opacity,
      this.templeSpreadDeg,
      this.solved.metrics.headWidthMm,
    );
      this.lastDrawTs = this.lockShownTs;
      this.lastDrawOpacity = opacity;
      this.lastDrawAt = now;
      this.drawnFrames++;
      const visibleNow = Boolean(this.renderer && hasPose && opacity >= 0.5);
      if (this.measurementVisible && !visibleNow && !document.hidden) this.visibilityInterruptions++;
      this.measurementVisible = visibleNow;
      if (this.measurementVisible && this.firstVisibleAt === null) this.firstVisibleAt = performance.now();
    }
    const k = this.solver.getIntrinsics();
    if (hasPose && k && (imageChanged || !this.lockActive)) {
      const p = project(k, this.smoothed.position);
      this.jitterRing.push(p.u, p.v);
    }
    if (this.pendingSnapshots.length > 0 && this.renderer) {
      // Same task as the render above: with preserveDrawingBuffer=false this is the only safe moment to read back.
      const snapshot = this.renderer.snapshot(this.lockActive ? this.ui.videoCanvas : this.ui.video, this.config.mirror);
      const waiting = this.pendingSnapshots;
      this.pendingSnapshots = [];
      for (const resolve of waiting) resolve(snapshot);
    }

    if (now - this.lastSceneLightUpdate > 400 && this.renderer && k) {
      this.lastSceneLightUpdate = now;
      const b = this.solved.bounds;
      const bounds = this.hasSolved && b.w > 0 ? { x: b.x / k.width, y: b.y / k.height, w: b.w / k.width, h: b.h / k.height } : null;
      const light = this.grabber.sceneLight(bounds);
      if (light) this.renderer.setSceneLight(light.faceLuma, light.tint, light.dirX, light.dirY);
    }
    if (now - this.lastUiUpdate > 100) {
      this.lastUiUpdate = now;
      this.showGuidance();
      this.debug?.update();
      this.sizing.updateCalibration(this.solver.depth.calibrated);
      if (this.showLandmarks) this.drawLandmarks();
    }
  }

  private updateGovernor(now: number): void {
    // A110W: held while the engine warms up, armed once the first frames
    // (first-use costs) are behind, on fresh measurements.
    if (this.governor.isHeld && this.startupPhase.governorReady(now) && !document.hidden) {
      this.perf.resetTiming();
      this.governor.arm(now);
      this.lastGovernorUpdate = now;
    }
    // The governor judges the device from the frame rate: while the tab is
    // hidden the engine runs on the 1 Hz background ticks, which would read as
    // a hopeless device and strip the shadow and the resolution from the
    // captures taken there (and from the first seconds after the tab returns).
    if (now - this.lastGovernorUpdate > 1000 && !document.hidden) {
      this.lastGovernorUpdate = now;
      const snap = this.perf.snapshot();
      this.governor.update(snap, now);
      // Slow devices: loss detection must outlive the gap between inferences.
      this.state.params.coastMs = Math.max(160, this.smoother.effectiveCoastMs);
    }
  }

  private countFirstSeconds(now: number): void {
    const f = this.firstSeconds;
    if (f.done || document.hidden) return;
    if (f.start < 0) { f.start = f.last = now; return; }
    const dt = now - f.last;
    f.last = now;
    f.frames++;
    if (dt > 50) f.over50++;
    if (dt > 100) f.over100++;
    if (dt > f.maxMs) f.maxMs = dt;
    if (now - f.start >= 5000) f.done = true;
  }

  /** Guidance bubble; while the glasses are being prepared (and nothing else to say), « Préparation de l’essayage… ». */
  private showGuidance(): void {
    if (!this.ui.recover.hidden) {
      this.ui.hint.hidden = true;
      return;
    }
    const hint = this.state.currentHint;
    if (hint === 'none' && (this.startupPhase.preparing || this.resuming)) {
      const text = this.resuming ? words().resuming : words().preparing;
      if (this.ui.hint.textContent !== text) this.ui.hint.textContent = text;
      this.ui.hint.hidden = false;
      return;
    }
    showHint(this.ui, hint);
  }

  private drawLandmarks(): void {
    const k = this.solver.getIntrinsics();
    if (!k) return;
    // A110Y: sized here, on first use (see applyVideoSize).
    if (this.ui.debugCanvas.width !== k.width || this.ui.debugCanvas.height !== k.height) {
      this.ui.debugCanvas.width = k.width;
      this.ui.debugCanvas.height = k.height;
    }
    const ctx = this.ui.debugCanvas.getContext('2d');
    const buf = this.lastLandmarks;
    if (!ctx) return;
    ctx.clearRect(0, 0, k.width, k.height);
    if (!buf) return;
    const W = k.width;
    const H = k.height;
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#00c8ff';
    for (const c of [LM.RIGHT_IRIS_CENTER, LM.LEFT_IRIS_CENTER]) {
      ctx.beginPath();
      ctx.arc(lmX(buf, c) * W, lmY(buf, c) * H, Math.max(2, this.solved.iris.meanPx / 2), 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.fillStyle = '#ff3b30';
    for (const i of [LM.RIGHT_EYE_INNER, LM.LEFT_EYE_INNER, LM.RIGHT_TEMPLE, LM.LEFT_TEMPLE, LM.RIGHT_EAR, LM.LEFT_EAR, LM.NOSE_TIP]) {
      ctx.fillRect(lmX(buf, i) * W - 3, lmY(buf, i) * H - 3, 6, 6);
    }
    if (this.hasSolved) {
      const p = project(k, this.smoothed.position);
      ctx.strokeStyle = '#34c759';
      ctx.beginPath();
      ctx.moveTo(p.u - 10, p.v);
      ctx.lineTo(p.u + 10, p.v);
      ctx.moveTo(p.u, p.v - 10);
      ctx.lineTo(p.u, p.v + 10);
      ctx.stroke();
    }
  }

  // ---------------------------------------------------------------- debug

  /** A110Y: QA-only behaviour (`?qa`, `?e2e`, `?debug`): background ticks, debug triggers. */
  private get qaMode(): boolean {
    if (this.config.debug) return true;
    try {
      const p = new URLSearchParams(location.search);
      return p.has('e2e') || p.has('qa') || p.has('debug') || p.has('measure');
    } catch {
      return false;
    }
  }

  private installDebugTriggers(): void {
    // A110Y: the debug panel (landmarks drawn on a full-size canvas, 100 ms updates) was one
    // long press on the title away for every customer; now only in QA.
    if (!this.qaMode) return;
    window.addEventListener('keydown', this.onKey);
    let pressTimer = 0;
    this.ui.product.addEventListener('pointerdown', () => {
      pressTimer = window.setTimeout(() => this.toggleDebug(), 700);
    });
    for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) {
      this.ui.product.addEventListener(ev, () => clearTimeout(pressTimer));
    }
    if (this.config.debug || new URLSearchParams(location.search).has('e2e')) {
      (window as unknown as { __mataTryOn: unknown }).__mataTryOn = {
        app: this,
        getStats: () => this.getStats(),
        captureOverlay: () => this.debugCaptureOverlay(),
        captureComposite: () => this.debugCaptureComposite(),
      };
    }
  }

  private readonly onKey = (e: KeyboardEvent) => {
    if (e.key === 'd' && !e.metaKey && !e.ctrlKey) this.toggleDebug();
  };

  private readonly onVisibility = () => {
    if (document.visibilityState === 'visible') {
      this.stopBackgroundTicks();
      if (!this.running) return;
      void this.ui.video.play().catch(() => undefined);
      if (this.hiddenAt >= 0) {
        const at = this.hiddenAt;
        this.hiddenAt = -1;
        this.wakeUp(at, performance.now() - at, 'hidden');
      }
    } else if (this.running) {
      if (this.hiddenAt < 0) {
        this.hiddenAt = performance.now();
        // Before the background ticks blur them: what this phone does when it is awake.
        this.sleepBaseline = { fps: this.perf.sessionAverageFps, inferMs: this.perf.inferMs };
      }
      // A110Y: grabbing, inferring and rendering a hidden page is for remote QA captures only.
      // A customer's hidden page now does nothing (the browser freezes timers anyway, and the
      // ticks it did let through piled up at the wake-up).
      if (this.qaMode) this.startBackgroundTicks();
    }
  };

  /** Back from the back/forward cache: the camera was stopped meanwhile (the watchdog restarts it). */
  private readonly onPageShow = (e: PageTransitionEvent) => {
    if (!e.persisted || !this.running || document.hidden) return;
    const at = this.hiddenAt >= 0 ? this.hiddenAt : this.lastTickAt;
    this.hiddenAt = -1;
    this.wakeUp(at, performance.now() - at, 'hidden');
  };


  // ---------------------------------------------------------------- A110X: after the phone slept

  /**
   * The page comes back after `gapMs` asleep (hidden since `atMs`, or a stall
   * of the render loop). What a page reload used to cure, without leaving:
   *  - the quality level the try-on had before the stall that preceded the
   *    sleep (a stalled page reads as a slow device to the governor);
   *  - fresh measurements;
   *  - fresh tracking (no stale pose, no stale frame-lock image): the glasses
   *    wait, hidden, « Reprise de l’essayage… », then fade in;
   *  - after a long sleep, a fresh tracker.
   * The watchdog then checks camera, tracker and 3D (EngineHealth).
   */
  private wakeUp(atMs: number, gapMs: number, reason: 'hidden' | 'stall'): void {
    if (!this.running || this.closed) return;
    const now = performance.now();
    // The next frame is not a stall of its own.
    this.lastTickAt = -1;
    const full = gapMs >= (reason === 'hidden' ? FULL_WAKE_HIDDEN_MS : FULL_WAKE_STALL_MS);
    // Watchdog clocks restart; speed is judged against the time before only after a real sleep.
    this.health.resumed(now, full ? this.sleepBaseline.fps : 0, full ? this.sleepBaseline.inferMs : 0);
    if (!full) {
      // A short stall (a heavy frame switch can cause one): fresh measurements
      // only; the glasses stay on screen and the quality as the governor set it.
      this.perf.resetTiming();
      this.perf.resetInference();
      this.lastGovernorUpdate = now;
      this.rearmVideoCallbacks();
      return;
    }
    const before = this.levelHistory.levelAt(atMs - LEVEL_LOOKBACK_MS);
    if (before < this.governor.levelIndex) this.governor.setLevel(before, 'wake-up');
    if (this.ui.video.paused) void this.ui.video.play().catch(() => undefined);
    this.beginResume(now);
    if (gapMs >= TRACKER_RESTART_SLEEP_MS && this.restartAllowed(now)) void this.restartTracker('sleep');
    track('tryon_recover', { part: 'resume', reason, duration_ms: Math.round(gapMs) });
  }

  /**
   * Fresh start of the tracking side (wake-up, restarted camera or tracker,
   * restored 3D): the start-up gate again — glasses hidden until the tracker
   * answers at a steady pace, then the fade — with fresh measurements and the
   * governor held until they are meaningful.
   */
  private beginResume(now: number): void {
    this.resumedAt = now;
    this.startupPhase = new StartupPhase();
    this.startupPhase.loopStarted(now);
    if (!(this.renderer?.contextLost ?? false) && this.warmDone) this.startupPhase.programsReady(now);
    this.resuming = true;
    this.governor.hold();
    this.perf.resetTiming();
    this.perf.resetInference();
    this.lastGovernorUpdate = now;
    this.smoother.reset();
    this.renderer?.occluder.reset();
    this.hasSolved = false;
    this.lockLeave();
    this.lockPending = null;
    for (const slot of this.lockRing) slot.captureTs = -1;
    this.lastVideoTime = -1;
    this.state.start(now);
    this.rearmVideoCallbacks();
  }

  /** A new chain of video-frame callbacks (a chain can die silently while the page sleeps). */
  private rearmVideoCallbacks(): void {
    const video = this.ui.video as HTMLVideoElement & { cancelVideoFrameCallback?: (id: number) => void };
    if (this.rvfcId && typeof video.cancelVideoFrameCallback === 'function') video.cancelVideoFrameCallback(this.rvfcId);
    this.rvfcId = 0;
    this.lastVideoCallbackAt = performance.now();
    if (this.running) this.scheduleGrab();
  }

  /** A201: the order sheet is being drawn, or was a moment ago (its last long frame). */
  private composingNow(now: number): boolean {
    return this.composing > 0 || now - this.composedAt < 1500;
  }

  /** Each frame while visible: the video-frame callbacks keep coming, and 4 × per second the watchdog. */
  private watch(now: number): void {
    if (!this.running) return;
    const video = this.ui.video;
    if (this.rvfcId !== 0 && now - this.lastVideoCallbackAt > 1500 && now - this.lastRvfcRearm > 1500 && video.readyState >= 2) {
      this.lastRvfcRearm = now;
      this.rearmVideoCallbacks();
    }
    if (now - this.lastHealthCheck < 250) return;
    this.lastHealthCheck = now;
    const stream = video.srcObject;
    const cam = stream instanceof MediaStream ? stream.getVideoTracks()[0] : undefined;
    const verdict = this.health.observe({
      nowMs: now,
      active: !this.closed && !this.lensView && !this.cameraRestarting && !this.composingNow(now),
      videoTime: video.currentTime,
      trackLive: !cam || (cam.readyState === 'live' && !cam.muted),
      framesSent: this.framesSent,
      results: this.resultCount,
      trackerBusyMs: this.tracker.busyForMs(now),
      // The first inferences of a session can take seconds on a slow phone: not a hang.
      // (A tracker whose restart failed counts as ready: it never answers, which leads to the message.)
      trackerReady: !this.trackerRestarting && !(this.startupPhase === this.sessionPhase && !this.startupPhase.trackerWarm && now - this.sessionLoopAt < 10_000),
      contextLost: this.renderer?.contextLost ?? false,
      fps: this.perf.fps,
      inferMs: this.startupPhase.trackerWarm ? this.perf.inferMs : 0,
    });
    for (const action of verdict.actions) {
      if (!this.restartAllowed(now)) break;
      if (action === 'restart-camera') void this.restartCamera('watchdog');
      else void this.restartTracker('watchdog');
    }
    if (this.reloadProblem === null) this.setRecover(verdict.message ? verdict.problem : null);
  }

  /**
   * A110Y: cap on the automatic restarts (camera or tracker): 3 in 5 minutes. A phone that
   * throttles can otherwise loop restart → reload of the engine (7 MB, a GPU context) → slow →
   * restart, each cycle a CPU and memory peak; past the cap the message asks to refresh the page.
   */
  private restartAllowed(now: number): boolean {
    if (this.restartsCapped) return false;
    while (this.restartLog.length > 0 && now - this.restartLog[0]! > RESTART_WINDOW_MS) this.restartLog.shift();
    if (this.restartLog.length >= MAX_RESTARTS_PER_WINDOW) {
      this.restartsCapped = true;
      track('tryon_error', { code: 'restarts', message: `${this.restartLog.length} restarts in ${Math.round(RESTART_WINDOW_MS / 60_000)} min` });
      this.setReload('restarts');
      return false;
    }
    this.restartLog.push(now);
    return true;
  }

  /** A110Y: the message that only a page refresh can clear (its button reloads). */
  private reloadProblem: ReloadProblem | null = null;
  private setReload(problem: ReloadProblem): void {
    if (this.closed || this.reloadProblem !== null) return;
    this.reloadProblem = problem;
    const w = words();
    this.ui.recoverText.textContent = w.reloadText;
    this.ui.recoverLabel.textContent = w.reloadButton;
    this.ui.recover.hidden = false;
    this.ui.hint.hidden = true;
    track('tryon_recover_message', { problem });
  }

  /**
   * A110Y: JS heap watch (Chrome / Android only: `performance.memory`). Past 70 % of the limit, or
   * +250 MB since the opening: one quality level down, and if it happens again the refresh message.
   */
  private checkMemory(now: number): void {
    const mem = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    if (!mem || !mem.jsHeapSizeLimit) return;
    if (this.heapAtOpen === 0) {
      this.heapAtOpen = mem.usedJSHeapSize;
      return;
    }
    const high = mem.usedJSHeapSize > mem.jsHeapSizeLimit * 0.7 || mem.usedJSHeapSize - this.heapAtOpen > 250e6;
    if (!high || now - this.memoryStepDownAt < 30_000) return;
    const used = Math.round(mem.usedJSHeapSize / 1e6);
    track('tryon_error', { code: 'memory', message: `heap ${used} MB / limit ${Math.round(mem.jsHeapSizeLimit / 1e6)} MB` });
    if (this.memoryStepDownAt > 0) {
      // Second time: the session is running away; a refresh is the honest cure.
      this.setReload('memory');
      return;
    }
    this.memoryStepDownAt = now;
    if (this.governor.levelIndex < QUALITY_LEVELS.length - 1) this.governor.setLevel(this.governor.levelIndex + 1, 'memory');
  }

  /** New camera stream (the old one ended, stays muted or froze while the phone slept). */
  private async restartCamera(reason: string): Promise<void> {
    if (this.cameraRestarting || this.closed || !this.running) return;
    this.cameraRestarting = true;
    try {
      const size = await this.camera.start();
      if (this.closed) { this.camera.stop(); return; }
      if (!this.running) return; // paused meanwhile (« Voir le paysage » runs its own camera)
      this.applyVideoSize(size.width, size.height);
      this.health.cameraRestarted(performance.now());
      this.beginResume(performance.now());
      track('tryon_recover', { part: 'camera', reason });
    } catch (err) {
      if (this.closed || !this.running) return;
      console.warn('[tryon:recover] camera', err);
      track('tryon_error', { code: 'camera_restart', message: String(err) });
      // Left to the watchdog: a camera that stays frozen becomes the « Relancer » message.
      this.health.cameraRestarted(performance.now());
    } finally {
      this.cameraRestarting = false;
    }
  }

  /** New tracker (hanging, or slow since the sleep: its worker may have lost its GPU context). */
  private async restartTracker(reason: string): Promise<void> {
    if (this.trackerRestarting || this.closed) return;
    this.trackerRestarting = true;
    const t0 = performance.now();
    try {
      this.trackerStatus = await this.tracker.restart();
      if (this.closed) return;
      this.health.trackerRestarted(performance.now());
      if (this.running) this.beginResume(performance.now());
      track('tryon_recover', { part: 'tracker', reason, duration_ms: Math.round(performance.now() - t0), tracker_mode: this.trackerStatus.mode, delegate: this.trackerStatus.delegate });
    } catch (err) {
      if (this.closed || /restarted|disposed/i.test(String(err))) return;
      console.warn('[tryon:recover] tracker', err);
      track('tryon_error', { code: 'tracker_restart', message: String(err) });
      this.health.trackerRestarted(performance.now());
    } finally {
      this.trackerRestarting = false;
    }
  }

  /** WebGL context lost / restored by the browser (phones reclaim it from pages left in the background). */
  private onContextChange(lost: boolean): void {
    if (this.closed) return;
    if (lost) {
      console.warn('[tryon:gl] context lost');
      this.hasSolved = false;
      this.contextLosses++;
      // A110Y: twice in a session: the page hosts too many contexts (or the GPU process fell) —
      // a refresh is the cure, not a relaunch.
      if (this.contextLosses >= 2) this.setReload('context');
      return;
    }
    console.info('[tryon:gl] context restored');
    track('tryon_recover', { part: 'render', reason: 'context_restored' });
    // three.js re-creates its GPU state; the programs are compiled again (hidden) before the glasses come back.
    this.warmCompiled = false;
    this.warmDone = false;
    if (this.running) this.beginResume(performance.now());
    void this.compileGpu();
  }

  /** The « Relancer » message, for what the automatic cure could not fix. */
  private setRecover(problem: HealthProblem | null): void {
    const show = problem !== null;
    if (show === !this.ui.recover.hidden) return;
    if (show && this.recoverShownBefore) {
      // A110Y: the relaunch did not hold — the problem is back. Say so, and offer the refresh.
      this.setReload('again');
      return;
    }
    this.ui.recover.hidden = !show;
    if (show) {
      this.recoverShownBefore = true;
      this.ui.hint.hidden = true;
      track('tryon_recover_message', { problem: problem ?? undefined });
    }
  }

  /**
   * « Relancer l’essayage »: new camera stream and tracker, the quality of an
   * opening — a page reload without leaving the page. A 3D context the browser
   * never gave back leaves only the reload itself.
   */
  private async relaunch(): Promise<void> {
    if (this.closed || !this.running) return;
    this.ui.recover.hidden = true;
    if (this.renderer?.contextLost || this.reloadProblem !== null) {
      track('tryon_recover', { part: 'all', reason: 'reload', problem: this.reloadProblem ?? undefined });
      location.reload();
      return;
    }
    track('tryon_recover', { part: 'all', reason: 'button' });
    // A phone that is simply slow is not told so twice.
    this.health.stopSlowChecks();
    const initial = QualityGovernor.guessInitialLevel();
    if (initial < this.governor.levelIndex) this.governor.setLevel(initial, 'relaunch');
    this.health.resumed(performance.now(), 0, 0);
    await Promise.all([this.restartCamera('button'), this.restartTracker('button')]);
  }

  private toggleDebug(): void {
    if (!this.debug) this.openDebug();
    else this.debug.toggle();
  }

  private openDebug(): void {
    if (!this.debug) {
      this.debug = new DebugPanel(this.ui.debugHost, {
        smoother: this.smoother,
        solver: this.solver,
        governor: this.governor,
        getOccluder: () => this.renderer?.occluder.params ?? { ...DEFAULT_OCCLUDER_PARAMS },
        patchOccluder: (patch) => this.renderer?.occluder.setParams(patch),
        setShowLandmarks: (v) => {
          this.showLandmarks = v;
          this.ui.debugCanvas.hidden = !v;
        },
        setNumFaces: (n) => this.tracker.setNumFaces(n),
        patchFit: (patch: Partial<FrameFit>) => {
          const spec = this.solver.getFrameSpec();
          this.solver.setFrameSpec({ ...spec, fit: { ...spec.fit, ...patch } });
        },
        getFit: () => this.solver.getFrameSpec().fit,
        getStats: () => this.getStats(),
        capture: () => this.debugCaptureComposite(),
        setPhysicalLenses: (enabled) => this.setPhysicalLenses(enabled),
        getPhysicalLenses: () => this.physicalLensesWanted,
        setCameraFov: (degrees) => {
          this.solver.setLongSideFov(degrees);
          const k = this.solver.getIntrinsics();
          if (k) this.renderer?.setVideo(k.width, k.height, k);
        },
        startTrace: () => {
          const k = this.solver.getIntrinsics();
          if (k) this.trace.start({ videoWidth: k.width, videoHeight: k.height, longSideFovDeg: this.solver.options.longSideFovDeg, frameId: this.config.frame.id }, performance.now());
        },
        stopTrace: () => this.trace.stop(),
      });
    }
    this.debug.show();
  }

  /** Opt-in measurement comparison; never enables itself on the storefront. */
  setEdgeAATest(enabled: boolean): void {
    this.edgeAATest = enabled;
    this.applyQuality(this.governor.current, 'edge-AA comparison');
    this.lastDrawAt = -Infinity;
  }

  setPhysicalLenses(enabled: boolean): void {
    this.physicalLensesWanted = enabled;
    if (this.renderer) this.renderer.physicalLenses = enabled;
    this.lastDrawAt = -Infinity;
  }

  /** Scalar test data only; no biometric measurements or camera pixels. */
  getMeasurement() {
    return { startup: { ...this.startup }, drawnFrames: this.drawnFrames, firstVisibleAt: this.firstVisibleAt,
      visible: this.running && this.measurementVisible, interruptions: this.visibilityInterruptions, losses: this.state.stats.losses,
      cameraFps: this.camera.getSettings()?.frameRate ?? null,
      inferenceFps: this.perf.snapshot().inferPerSecond,
      edgeAATest: this.edgeAATest, physicalLenses: this.physicalLensesWanted, quality: this.governor.current.name,
      // A110W start-up smoothness (scalars only).
      firstSeconds: this.firstSeconds.done ? { fps: this.firstSeconds.frames / ((this.firstSeconds.last - this.firstSeconds.start) / 1000), over50: this.firstSeconds.over50, over100: this.firstSeconds.over100, worstMs: this.firstSeconds.maxMs, qualityChanges: this.firstSeconds.qualityChanges } : null,
      preparedMs: this.startupPhase.revealedAt !== null && this.firstSeconds.start >= 0 ? this.startupPhase.revealedAt - this.firstSeconds.start : null };
  }

  getStats(): DebugStats {
    const p = this.perf.snapshot();
    const k = this.solver.getIntrinsics();
    const inferSize = this.grabber.targetSize();
    return {
      fps: p.fps,
      inferMs: p.inferMs,
      inferP95Ms: p.inferP95Ms,
      inferPerSecond: p.inferPerSecond,
      latencyMs: p.latencyMs,
      mode: this.trackerStatus?.mode ?? '—',
      delegate: this.trackerStatus?.delegate ?? '—',
      state: this.state.current,
      hint: this.state.currentHint,
      yaw: this.solved.yawDeg,
      pitch: this.solved.pitchDeg,
      roll: this.solved.rollDeg,
      depthMm: this.solved.depthMm,
      irisPx: this.solved.iris.meanPx,
      irisReliable: this.solved.irisReliable,
      pdMm: this.solved.metrics.pdMm,
      faceWidthMm: this.solved.metrics.faceWidthMm,
      headWidthMm: this.solved.metrics.headWidthMm,
      calibrated: this.solved.calibrated,
      quality: this.governor.current.name,
      spreadDeg: this.templeSpreadDeg,
      still: this.smoothed.still,
      coasting: this.smoothed.coasting,
      predictedMs: this.smoothed.predictedMs,
      losses: this.state.stats.losses,
      lastReacquireMs: this.state.stats.lastReacquireMs,
      videoSize: k ? `${k.width}x${k.height}` : '—',
      inferSize: `${inferSize.width}x${inferSize.height}`,
      jitterPx: this.jitterRing.rms(),
      rawJitterPx: this.rawJitterRing.rms(),
      depthScale: this.solver.depth.scaleRatio,
      meshDepthMm: this.renderer?.occluder.noseToEarMm ?? null,
      meshDepthCorrection: this.renderer?.occluder.depthCorrection ?? null,
      anchorPoints: this.hasSolved ? this.solved.anchorPoints : null,
      anchorSpreadMm: this.hasSolved ? this.solved.anchorSpreadMm : null,
      frameLock: this.lockActive,
      displayFps: this.displayTimes.filter(t => performance.now() - t < 1000).length,
      drawnFrames: this.drawnFrames,
      displayedFrameAgeMs: this.lockActive ? Math.max(0, performance.now() - this.lockShownTs) : null,
      cameraFps: this.camera.getSettings()?.frameRate ?? null,
      gl: this.renderer?.stats() ?? null,
      restarts: this.restartLog.length,
      contextLosses: this.contextLosses,
    };
  }

  /**
   * Debug/e2e: re-renders the current pose and returns the overlay as a PNG
   * data URL (the drawing buffer is only readable right after a render).
   */
  debugCaptureOverlay(): string | null {
    if (!this.renderer) return null;
    const now = performance.now();
    const hasPose = this.hasSolved && this.smoother.evaluate((this.lockActive ? this.lockShownTs : now) / 1000, this.smoothed);
    const savedBackground = this.renderer.scene.background;
    this.renderer.scene.background = null;
    this.renderer.render(
      hasPose ? this.smoothed.position : null,
      hasPose ? this.smoothed.quaternion : null,
      this.solver.getFrameSpec(),
      this.state.opacity(now),
      this.templeSpreadDeg,
      this.solved.metrics.headWidthMm,
    );
    const png = this.renderer.canvas.toDataURL('image/png');
    this.renderer.scene.background = savedBackground;
    this.lastDrawTs = -1;
    return png;
  }

  /**
   * Debug/e2e: composite of the current video frame and the overlay, mirrored
   * like the UI, captured by the render loop itself (resolves after the next
   * frame; falls back to an immediate render when the loop is paused).
   */
  debugCaptureComposite(): Promise<string | null> {
    return this.capturePhoto().then((snapshot) => snapshot?.toDataURL('image/png') ?? null);
  }

  /**
   * A110V: photo of the wearer with the frame — the current camera frame and
   * the 3D overlay, mirrored like the screen, as a canvas at the camera's
   * resolution. Captured by the render loop itself, right after a real render
   * (resolves after the next frame; immediate render when the loop is paused).
   * The pixels stay in the page: nothing is uploaded or logged.
   */
  capturePhoto(): Promise<HTMLCanvasElement | null> {
    if (!this.renderer) return Promise.resolve(null);
    if (!this.running || document.visibilityState !== 'visible') {
      this.lastDrawAt = -Infinity;
      this.tick(performance.now());
      return Promise.resolve(this.renderer ? this.renderer.snapshot(this.lockActive ? this.ui.videoCanvas : this.ui.video, this.config.mirror) : null);
    }
    return new Promise((resolve) => {
      this.pendingSnapshots.push(resolve);
      // Safety net: never leave a caller hanging if the loop stops.
      setTimeout(() => resolve(null), 2000);
    });
  }

  /**
   * A110V: centre of the tracked face in the photo (fractions, mirrored like
   * the photo), so that the photo is framed on the face; null while no face is tracked.
   */
  private photoFocus(): { x: number; y: number } | null {
    const k = this.solver.getIntrinsics();
    const b = this.solved.bounds;
    if (!k || !this.hasSolved || this.state.current !== 'tracking' || !(b.w > 0) || !(b.h > 0)) return null;
    const cx = (b.x + b.w / 2) / k.width;
    return { x: this.config.mirror ? 1 - cx : cx, y: (b.y + b.h / 2) / k.height };
  }

  // ---------------------------------------------------------------- A110T « Voir le paysage »

  /**
   * Pauses the try-on (front camera off: phones run one camera at a time),
   * shows the rear camera through the chosen Sunset lens, then resumes the
   * try-on with the same model, size, PD and colours.
   */
  async openLensView(): Promise<void> {
    if (this.closed || !this.running || this.lensView || this.lensViewOpening || !this.materials) return;
    this.lensViewOpening = true;
    const choice = this.materials.current();
    const lens = isSunsetLens(choice.lens) ? choice.lens : 'sunset';
    const density = lensViewDensity(choice.density);
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.pauseForLensView();
    const view = new LensView(this.ui.root, {
      lens,
      density,
      onSelect: (id, d) => this.materials?.selectSunset(id, d),
      onClose: () => {
        if (this.lensView === view) this.lensView = null;
        void this.resumeAfterLensView().then(() => { if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true }); });
      },
    });
    this.lensView = view;
    this.lensViewOpening = false;
    await view.open();
  }

  private pauseForLensView(): void {
    this.stopLoops();
    this.state.stop(performance.now());
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.lockLeave();
    this.lockPending = null;
    this.camera.stop();
  }

  private async resumeAfterLensView(): Promise<void> {
    if (this.closed || this.running) return;
    let size: { width: number; height: number };
    try {
      size = await this.camera.start();
    } catch (err) {
      if (this.closed) return;
      const code = err instanceof CameraError ? err.code : 'unknown';
      const key: StringKey =
        code === 'denied' ? 'error_denied' : code === 'no_device' ? 'error_no_device' : code === 'insecure_context' ? 'error_insecure' : code === 'in_use' ? 'error_in_use' : 'error_unsupported';
      showError(this.ui, key, false);
      return;
    }
    if (this.closed) { this.camera.stop(); return; }
    this.applyVideoSize(size.width, size.height);
    this.smoother.reset();
    this.renderer?.occluder.reset();
    this.hasSolved = false;
    this.lastVideoTime = -1;
    // The pause would otherwise enter the frame-time ring as one interval of
    // its whole duration: a "slow device" for the governor, and a step down.
    this.perf.resetTiming();
    this.lastGovernorUpdate = performance.now();
    this.lastDrawAt = -Infinity;
    this.running = true;
    this.state.start(performance.now());
    this.scheduleGrab();
    this.rafId = requestAnimationFrame((t) => this.renderLoop(t));
    document.addEventListener('visibilitychange', this.onVisibility);
    if (document.visibilityState !== 'visible') this.onVisibility();
  }

  // ---------------------------------------------------------------- teardown

  private stopLoops(): void {
    this.running = false;
    this.lastTickAt = -1;
    this.hiddenAt = -1;
    this.stopBackgroundTicks();
    cancelAnimationFrame(this.rafId);
    const video = this.ui.video as HTMLVideoElement & { cancelVideoFrameCallback?: (id: number) => void };
    if (this.rvfcId && typeof video.cancelVideoFrameCallback === 'function') video.cancelVideoFrameCallback(this.rvfcId);
    this.rvfcId = 0;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.headSetup.destroy();
    this.frameSwap.cancel();
    const wasRunning = this.running;
    this.stopLoops();
    this.state.stop(performance.now());
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('keydown', this.onKey);
    this.offContext?.();
    this.offContext = null;
    this.ui.recover.hidden = true;
    this.lensView?.destroy();
    this.lensView = null;
    this.order?.destroy();
    this.order = null;
    this.engraveScreen?.destroy(); this.engraveScreen = null;
    this.engravePanel?.destroy(); this.engravePanel = null;
    this.releaseFocus?.(); this.releaseFocus = null;
    if (this.engrave3dPending) cancelAnimationFrame(this.engrave3dPending);
    this.engrave3d.dispose();
    this.engraveBand?.destroy(); this.engraveBand = null;
    window.clearTimeout(this.engraveToastTimer);
    this.shell.destroy();
    this.unsubscribeSession();
    this.camera.stop();
    this.trace.clear();
    this.tracker.dispose();
    this.trackerReady = null;
    this.appearance?.dispose(); this.appearance = null;
    // A110Y: counted before the renderer goes (what was still alive on the GPU at the close).
    const gl = this.renderer?.stats() ?? null;
    this.renderer?.dispose();
    this.renderer = null;
    this.frame = null;
    if (wasRunning) {
      track('tryon_close', {
        duration_ms: Math.round(performance.now() - this.openedAt),
        avg_fps: Math.round(this.perf.sessionAverageFps),
        infer_ms: Math.round(this.perf.inferMs),
        losses: this.state.stats.losses,
        quality_level: this.governor.current.name,
        ...(gl ? { gl_textures: gl.textures, gl_geometries: gl.geometries, gl_programs: gl.programs, gl_pixels: gl.bufferPixels } : {}),
        restarts: this.restartLog.length,
        context_losses: this.contextLosses,
      });
    }
    for (const resolve of this.pendingSnapshots) resolve(null);
    this.pendingSnapshots = [];
    // A110Y: the 2D canvases and the video give their memory back now, not at the garbage
    // collection (late on Safari, where three full-resolution canvases are 10 MB).
    for (const slot of this.lockRing) {
      slot.canvas.width = 0;
      slot.canvas.height = 0;
    }
    this.lockRing.length = 0;
    this.lockPending = null;
    this.videoCtx = null;
    for (const c of [this.ui.videoCanvas, this.ui.debugCanvas]) {
      c.width = 0;
      c.height = 0;
    }
    try {
      const video = this.ui.video;
      video.pause();
      video.srcObject = null;
      video.removeAttribute('src');
      video.load();
    } catch {
      /* ignore */
    }
    this.ui.root.remove();
    this.config.onClose?.();
  }
}

/** RMS distance of 2D samples from their mean over a sliding window (jitter in pixels). */
class JitterMeter {
  private readonly u: number[] = [];
  private readonly v: number[] = [];
  constructor(private readonly size: number) {}
  push(u: number, v: number): void {
    this.u.push(u);
    this.v.push(v);
    if (this.u.length > this.size) {
      this.u.shift();
      this.v.shift();
    }
  }
  rms(): number {
    const n = this.u.length;
    if (n < 5) return 0;
    let mu = 0;
    let mv = 0;
    for (let i = 0; i < n; i++) {
      mu += this.u[i]!;
      mv += this.v[i]!;
    }
    mu /= n;
    mv /= n;
    let acc = 0;
    for (let i = 0; i < n; i++) acc += (this.u[i]! - mu) ** 2 + (this.v[i]! - mv) ** 2;
    return Math.sqrt(acc / n);
  }
}

/**
 * Folder that contains `assets/`, `wasm/` and `models/`: one level above the
 * bundle when it is served as an external module (CDN), else Vite's base.
 */
function defaultAssetBase(): string {
  const self = import.meta.url;
  if (/\.js(\?|$)/.test(self)) return new URL('../', self).href;
  return import.meta.env.BASE_URL;
}
